import { QueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

/**
 * Global error handler for React Query
 * Displays user-friendly toast notifications based on error type
 */
function handleQueryError(error: Error): void {
  console.error('[Query Error]', error);

  // Don't show toast for AbortError (user navigated away)
  if (error.name === 'AbortError') return;

  // Handle specific error types
  if (error.message.includes('401') || error.message.includes('Unauthorized')) {
    toast.error('Session expired. Please log in again.');
    return;
  }

  if (error.message.includes('403') || error.message.includes('Forbidden')) {
    toast.error('You do not have permission to perform this action.');
    return;
  }

  if (error.message.includes('Network') || error.message.includes('Failed to fetch')) {
    toast.error('Connection error. Check your internet connection.');
    return;
  }

  if (error.message.includes('500') || error.message.includes('Internal Server')) {
    toast.error('Server error. Please try again later.');
    return;
  }

  // Generic error
  toast.error(error.message || 'Error al cargar datos');
}

/**
 * React Query client with optimized defaults for Relo
 *
 * Configuration rationale:
 * - staleTime: 30s - Data considered fresh for 30 seconds (matches current manual cache)
 * - gcTime: 5min - Keep unused data in memory for quick navigation
 * - retry: 1 - Single retry with exponential backoff
 * - refetchOnWindowFocus: true - Natural stale-while-revalidate behavior
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 30 seconds
      staleTime: 30 * 1000,

      // Keep data in memory for 5 minutes after component unmounts
      gcTime: 5 * 60 * 1000,

      // Single retry with exponential backoff
      retry: 1,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),

      // Refetch when window regains focus (SWR behavior)
      refetchOnWindowFocus: true,

      // Refetch when component mounts if data is stale
      refetchOnMount: true,

      // Don't refetch when reconnecting (can be noisy)
      refetchOnReconnect: false,

      // Throw errors to error boundaries if present
      throwOnError: false,
    },
    mutations: {
      // Default error handler for mutations
      onError: (error: Error) => {
        console.error('[Mutation Error]', error);
        toast.error(error.message || 'Error al guardar cambios');
      },
    },
  },
});

// Expose error handler for custom use in hooks
export { handleQueryError };
